// Address hygiene for the lead forms. This is deliberately NOT a deliverability
// service — it uses the free Zippopotam ZIP lookup the forms already rely on,
// plus format heuristics, to catch the failure modes that actually show up in
// lead data: fake or mistyped ZIPs, street lines with no house number, and
// keyboard mashing. It cannot confirm that a real, deliverable address exists.

export interface FieldCheck {
  valid: boolean
  /** User-facing message when invalid. */
  message: string
}

const VALID: FieldCheck = { valid: true, message: '' }

// Deliberately permissive: a PO box, rural route, or apartment line must all
// pass. We only reject lines that cannot plausibly be a street address.
export function validateStreetAddress(raw: string): FieldCheck {
  const value = raw.trim().replace(/\s+/g, ' ')
  if (!value) {
    return { valid: false, message: 'Please enter your street address' }
  }
  if (value.length < 5) {
    return {
      valid: false,
      message: 'Please enter your full street address, including the number',
    }
  }
  // Any character repeated four or more times is keyboard mashing, not a street.
  if (/(.)\1{3,}/i.test(value.replace(/\s/g, ''))) {
    return { valid: false, message: 'Please enter a valid street address' }
  }
  const isPoBox = /\b(p\.?\s?o\.?\s?box|post\s+office\s+box)\b/i.test(value)
  const hasNumber = /\d/.test(value)
  const letterWords = value.match(/[a-z]{2,}/gi) || []
  if (!isPoBox && !hasNumber) {
    return {
      valid: false,
      message: 'Please include the street number (e.g. 123 Main St)',
    }
  }
  if (letterWords.length === 0) {
    return {
      valid: false,
      message: 'Please include the street name (e.g. 123 Main St)',
    }
  }
  return VALID
}

export function validateZipCode(raw: string): FieldCheck {
  const value = raw.trim()
  if (!value) return { valid: false, message: 'Please enter your ZIP code' }
  if (!/^\d{5}$/.test(value)) {
    return { valid: false, message: 'Please enter a valid 5-digit ZIP code' }
  }
  return VALID
}

export interface ZipPlace {
  city: string
  state: string
}

// Cached so re-validating on every Continue press doesn't re-request. `null`
// means the ZIP was checked and does not exist.
const zipCache = new Map<string, ZipPlace | null>()

// Resolves a ZIP to its city/state. `null` = the ZIP does not exist;
// `undefined` = the lookup was unreachable, which callers must NOT treat as a
// validation failure.
export async function lookupZip(
  zip: string,
): Promise<ZipPlace | null | undefined> {
  if (!/^\d{5}$/.test(zip)) return null
  if (zipCache.has(zip)) return zipCache.get(zip) ?? null
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zip}`)
    if (!response.ok) {
      zipCache.set(zip, null)
      return null
    }
    const data = await response.json()
    const place = data?.places?.[0]
    if (!place) {
      zipCache.set(zip, null)
      return null
    }
    const result: ZipPlace = {
      city: place['place name'] || '',
      state: place['state abbreviation'] || '',
    }
    zipCache.set(zip, result)
    return result
  } catch {
    // Network failure is NOT a validation failure — never block a lead because
    // a free third-party lookup is unreachable.
    return undefined
  }
}

export interface AddressCheck extends FieldCheck {
  /** Resolved city/state when the ZIP was verified, for filling the form. */
  place?: ZipPlace
}

// Full address check used on Continue/Submit. `state` is optional — pass it
// when the form lets the user choose a state, so a state that contradicts the
// ZIP can be caught.
export async function validateAddress(input: {
  streetAddress: string
  zipCode: string
  state?: string
}): Promise<AddressCheck> {
  const street = validateStreetAddress(input.streetAddress)
  if (!street.valid) return street

  const zip = validateZipCode(input.zipCode)
  if (!zip.valid) return zip

  const place = await lookupZip(input.zipCode.trim())
  // `undefined` = lookup unreachable; let the lead through rather than
  // blocking on a third-party outage.
  if (place === undefined) return VALID
  if (place === null) {
    return {
      valid: false,
      message: "We couldn't find that ZIP code. Please double-check it.",
    }
  }
  const chosenState = (input.state || '').trim().toUpperCase()
  if (chosenState && place.state && chosenState !== place.state.toUpperCase()) {
    return {
      valid: false,
      message: `ZIP ${input.zipCode.trim()} is in ${place.state}, not ${chosenState}. Please check your ZIP and state.`,
      place,
    }
  }
  return { ...VALID, place }
}
